import React, {useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import BoltIcon from '@mui/icons-material/Bolt';
import SavingsIcon from '@mui/icons-material/Savings';
import VerifiedIcon from '@mui/icons-material/Verified';

type Plan = {
  id: string;
  name: string;
  size: string;
  battery: string;
  monthlyPrice: number;
  idealFor: string;
};

const plans: Plan[] = [
  {
    id: 'starter',
    name: 'Starter Solar',
    size: '3kW system',
    battery: '3.5kWh backup',
    monthlyPrice: 0,
    idealFor: 'low usage homes',
  },
  {
    id: 'family',
    name: 'Family Solar',
    size: '5kW system',
    battery: '7kWh backup',
    monthlyPrice: 0,
    idealFor: 'Typical family usage and appliances',
  },
  {
    id: 'premium',
    name: 'Premium Solar+',
    size: '8kW system',
    battery: '10kWh backup',
    monthlyPrice: 0,
    idealFor: 'High-consumption homes',
  },
];

interface OwnProps {
  propertyLabel?: string;
}

const SolarRentalSection: React.FC<OwnProps> = ({propertyLabel}) => {
  const [selectedPlanId, setSelectedPlanId] = useState<string>('family');
  const [roofType, setRoofType] = useState<string>('tile');
  const [includeMaintenance, setIncludeMaintenance] = useState<boolean>(true);
  const [includeInsurance, setIncludeInsurance] = useState<boolean>(true);
  const [includeMonitoring, setIncludeMonitoring] = useState<boolean>(false);
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);

  const selectedPlan = useMemo(() => {
    return plans.find((plan) => plan.id === selectedPlanId) || plans[0];
  }, [selectedPlanId]);

  const addonsTotal =
    (includeMaintenance ? 12 : 0) +
    (includeInsurance ? 9 : 0) +
    (includeMonitoring ? 6 : 0);

  const monthlyTotal = selectedPlan.monthlyPrice + addonsTotal;

  const handleRoofType = (event: SelectChangeEvent) => {
    setRoofType(event.target.value);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        mb: 4,
      }}
    >
      <Stack spacing={3}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(120deg, #0f766e 0%, #0e9f6e 60%, #12b981 100%)',
            color: '#fff',
          }}
        >
          
          <Typography sx={{mt: 1, opacity: 0.95}}>
            Choose a packageand preview monthly rental pricing before checkout.
          </Typography>
        </Box>

        <Typography
          variant="h2"
          sx={{
            fontSize: '1.75rem',
            fontWeight: 600,
            color: '#0f766e',
            borderBottom: '2px solid #e0e0e0',
            paddingBottom: 1,
          }}
        >
          Solar Rental
        </Typography>

        <Grid container spacing={2}>
          {plans.map((plan) => {
            const isSelected = plan.id === selectedPlanId;
            return (
              <Grid item xs={12} md={4} key={plan.id}>
                <Card
                  sx={{
                    borderRadius: 2,
                    border: isSelected ? '2px solid #0f766e' : '1px solid #dbe4ea',
                    boxShadow: isSelected ? '0 10px 24px rgba(15, 118, 110, 0.2)' : '0 2px 10px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                  }}
                >
                  <CardActionArea onClick={() => setSelectedPlanId(plan.id)}>
                    <CardContent>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="h6" sx={{fontWeight: 700}}>{plan.name}</Typography>
                          {isSelected && <Chip size="small" label="Selected" sx={{bgcolor: '#dcfce7', color: '#166534', fontWeight: 700}} />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">{plan.idealFor}</Typography>
                        <Divider />
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SolarPowerIcon fontSize="small" sx={{color: '#0f766e'}} />
                          <Typography variant="body2">{plan.size}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BoltIcon fontSize="small" sx={{color: '#0f766e'}} />
                          <Typography variant="body2">{plan.battery}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{pt: 0.5}}>
                          <SavingsIcon fontSize="small" sx={{color: '#0f766e'}} />
                          <Typography sx={{fontWeight: 700, color: '#0f766e'}}>${plan.monthlyPrice}/month</Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
              <Typography variant="h6" sx={{fontWeight: 700, mb: 1.5}}>Property & Installation Details</Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Property"
                  value={propertyLabel || ''}
                  fullWidth
                  disabled
                />
                <FormControl fullWidth>
                  <InputLabel id="solar-roof-type-label">Roof Type</InputLabel>
                  <Select
                    labelId="solar-roof-type-label"
                    value={roofType}
                    label="Roof Type"
                    onChange={handleRoofType}
                  >
                    <MenuItem value="tile">Tile Roof</MenuItem>
                    <MenuItem value="metal">Metal Roof</MenuItem>
                    <MenuItem value="concrete">Concrete Slab</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="Preferred Installation Week" type="date" InputLabelProps={{shrink: true}} fullWidth />
                <TextField label="Contact Phone" placeholder="e.g. +263 77 123 4567" fullWidth />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
              <Typography variant="h6" sx={{fontWeight: 700, mb: 1}}>Add-ons</Typography>
              <Stack spacing={0.5}>
                <FormControlLabel
                  control={<Switch checked={includeMaintenance} onChange={(event) => setIncludeMaintenance(event.target.checked)} />}
                  label="Maintenance Cover (+$12/month)"
                />
                <FormControlLabel
                  control={<Switch checked={includeInsurance} onChange={(event) => setIncludeInsurance(event.target.checked)} />}
                  label="Panel Insurance (+$9/month)"
                />
                <FormControlLabel
                  control={<Switch checked={includeMonitoring} onChange={(event) => setIncludeMonitoring(event.target.checked)} />}
                  label="Premium Monitoring (+$6/month)"
                />
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid #d1fae5',
            bgcolor: '#f0fdf4',
          }}
        >
          <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} justifyContent="space-between">
            <Typography sx={{fontWeight: 600}}>Selected Plan: {selectedPlan.name}</Typography>
            <Typography sx={{fontWeight: 700, color: '#166534'}}>Estimated Monthly Total: ${monthlyTotal}</Typography>
          </Stack>
          <Typography variant="body2" sx={{mt: 0.75, color: '#14532d'}}>
            This is a visual demo. No order will be placed and no payment will be processed.
          </Typography>
        </Paper>

        <Alert severity="info">
          UI-only preview: checkout and contract generation are intentionally not wired to backend services yet.
        </Alert>

        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} alignItems={{xs: 'stretch', sm: 'center'}}>
          <FormControlLabel
            control={<Switch checked={acceptTerms} onChange={(event) => setAcceptTerms(event.target.checked)} />}
            label="I understand this is a preview and accept the sample terms"
          />
          <Box sx={{ml: {sm: 'auto'}}}>
            <Button
              variant="contained"
              sx={{
                bgcolor: '#0f766e',
                '&:hover': {bgcolor: '#0e8a65'},
              }}
              disabled={!acceptTerms}
            >
              Continue to Checkout (Preview)
            </Button>
          </Box>
        </Stack> */}
      </Stack>
    </Paper>
  );
};

export default SolarRentalSection;